import { createRoot, Root } from 'react-dom/client';

import { JSX, RefObject, useLayoutEffect, useMemo, useRef } from 'react';

import { OrgChart as D3OrgChart } from 'd3-org-chart';

import { buildChartRows, ChartRowsOptions } from './orgChartTree';
import { OrgChartTreeNode } from './types';

export type OrgChartProps<T extends OrgChartTreeNode<T>> = ChartRowsOptions<T> & {
  // The host's whole tree; undefined until it's built.
  tree: T | undefined;
  // Each node renders into its own React root, outside the host's providers: pass everything through props.
  renderComponent: (node: T) => JSX.Element;
  onNodeClick: (node: T) => void;
  nodeWidth?: (node: T) => number;
  nodeHeight?: (node: T) => number;
  childrenMargin?: number;
  siblingsMargin?: number;
  neighborMargin?: number;
  // The host keeps the d3 chart instance to call it directly (e.g. setCentered).
  chartRef: RefObject<D3OrgChart<T> | null>;
};

const nodeRoots = new Map<HTMLElement, Root>();

// d3-org-chart removes a card that leaves the chart in its exit transition's "end" handler. If a re-render brings
// that card back during the transition's last frame (e.g. paging back to cards that just paged out), the new
// render's transition hasn't started yet to interrupt it, so the stale handler still fires and removes a card the
// new render kept. Interrupting the cards' in-flight transitions first cancels those handlers; the new render then
// exits whatever it no longer shows.
function renderChart<T>(chart: D3OrgChart<T>) {
  chart.getChartState().nodesWrapper?.selectAll('g.node').interrupt();
  chart.render();
}

export default function OrgChart<T extends OrgChartTreeNode<T>>({
  tree,
  pageSize,
  displayLevel,
  keepChildrenWhole,
  getExtraChildren,
  nodeWidth,
  nodeHeight,
  childrenMargin = 40,
  siblingsMargin = 30,
  neighborMargin = 20,
  renderComponent,
  onNodeClick,
  chartRef,
}: OrgChartProps<T>): JSX.Element {
  const d3Container = useRef<HTMLDivElement>(null);

  const data = useMemo(
    () => (tree ? buildChartRows(tree, { pageSize, displayLevel, keepChildrenWhole, getExtraChildren }) : []),
    [tree, pageSize, displayLevel, keepChildrenWhole, getExtraChildren],
  );

  const lastSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!data.length || !d3Container.current) return;

    if (!chartRef.current) {
      chartRef.current = new D3OrgChart();
      chartRef.current.container(d3Container.current as unknown as string);
    }

    const chart = chartRef.current;

    chart
      .container(d3Container.current as unknown as string) // d3-org-chart types are wrong here does accept element ref and string
      .layout('top')
      .compact(false)
      .nodeWidth((d) => (nodeWidth ? nodeWidth(d.data) : 90)) // Use the custom nodeWidth
      .nodeHeight((d) => (nodeHeight ? nodeHeight(d.data) : 120)) // Use the custom nodeHeight
      .childrenMargin(() => childrenMargin)
      .siblingsMargin(() => siblingsMargin)
      .neighbourMargin(() => neighborMargin)
      .onNodeClick((d) => {
        onNodeClick(d.data);
      })
      .nodeButtonHeight(() => 0)
      .nodeButtonWidth(() => 0)
      .buttonContent(() => {
        return ``;
      })
      .linkUpdate((node, index, nodes) => {
        const currentNode = nodes[index] as unknown as SVGPathElement; // don't know why types are wrong here

        if (node.data.linked) {
          currentNode.setAttribute('fill', 'none');
          currentNode.setAttribute('stroke', '#EFEFEF');
          currentNode.setAttribute('stroke-width', '2');
        }
      })
      .linkYOffset(0)
      .duration(400)
      .nodeContent((d) => {
        const nodeID = `orgNode-${d.data.id}`;

        requestAnimationFrame(() => {
          const nodeElement = document.getElementById(nodeID);

          if (!nodeElement) return;

          let root = nodeRoots.get(nodeElement);

          if (!root) {
            root = createRoot(nodeElement);
            nodeRoots.set(nodeElement, root);
          }

          const renderedComponent = renderComponent(d.data);
          root.render(renderedComponent);
        });

        return `<div id="${nodeID}" style="width:100%; height:100%;"></div>`;
      })
      // Every row arrives expanded (d3-org-chart's own flag) so one render draws the whole tree.
      .data(data.map((row) => ({ ...row, _expanded: true })));

    renderChart(chart);
    // Resize observer to adjust chart dynamically
  }, [data, renderComponent]);

  useLayoutEffect(() => {
    if (!d3Container.current) return;

    // Threshold to ignore small changes (like scrollbar appearing/disappearing)
    const RESIZE_THRESHOLD = 20; // pixels

    const resizeObserver = new ResizeObserver((entries) => {
      const chart = chartRef.current;
      if (!chart) return;

      entries.forEach((entry) => {
        const { width, height } = entry.contentRect;
        const lastSize = lastSizeRef.current;

        // Calculate the difference from last known size
        const widthDiff = Math.abs(width - lastSize.width);
        const heightDiff = Math.abs(height - lastSize.height);

        // Only re-render if the change is significant (larger than scrollbar width)
        if (widthDiff > RESIZE_THRESHOLD || heightDiff > RESIZE_THRESHOLD) {
          lastSizeRef.current = { width, height };
          renderChart(chart.container(d3Container.current as unknown as string));
        }
      });
    });

    resizeObserver.observe(d3Container.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const container = d3Container.current;

    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel, { capture: true });
      }
    };
  }, []);

  return <div ref={d3Container} />;
}
